/**
 * Renders the calendar view for managing sprints, including filters and permissions.
 * It also handles interval-based auto-refresh of monthly sprint data.
 *
 * @component
 * @returns {JSX.Element} - The rendered sprint calendar interface.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Button, Space } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'
import { useStores } from '../../../cls/stores/MobXProvider'
import { actLoadSpecificMonthSprints } from '../../../stores/ui-lst-sprints/actions'
import { observer } from 'mobx-react-lite'
import {
	GeneralContentWrapper,
	GeneralTitle
} from '../../../components/Shared/GeneralComponents'
import { ArrowLeftOutlined } from '@ant-design/icons'
import CustomCalendar from '../../../components/Shared/CustomCalendar'
import dayjs from 'dayjs'
import { Filter } from '../../../components/Shared'
import { sprintsLstProgressOptions } from './SprintsLst'
import { newSprint } from '../../../stores/sprints/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const SprintsCalendar = observer(() => {
	const navigate = useNavigate()
	const { uiLstSprints, hasIntervalLoad, appType, users } = useStores()
	const { t } = useSmartTranslation()
	const { appIsClient, appIsTeam } = appType
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	// Determine th table entity for notification based on the app Type
	let tableEntity = appIsClient ? 'sprints' : appIsTeam ? 'sprints_team' : ''

	// If the user does not have personal permission
	// and lacks the required permissions for the action and resource
	// Display a message indicating lack of permissions
	if (!(isAdmin || isSuperAdmin || users.canUser('read', tableEntity))) {
		return (
			<div style={{ textAlign: 'center' }}>
				{t('__denied.access').charAt(0).toUpperCase() +
					t('__denied.access%__feature-off%_sprint')
						.toLowerCase()
						.slice(1)}
			</div>
		)
	}

	const intervalIdRef = useRef(null) // Use ref for intervalId
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [errorKey, setErrorKey] = useState(null)
	const withIntervalLoad = hasIntervalLoad('sprints')

	// State to manage the calendar states
	const [currentDateObject, setCurrentDateObject] = useState(dayjs())
	const [calendarPanel, setCalendarPanel] = useState('month')

	// Filter values
	const bySearch = uiLstSprints.search
	const byDel = uiLstSprints.del
	const byCategory = uiLstSprints.filter.category
	const byProgress = uiLstSprints.filter.progress
	const byPriority = uiLstSprints.filter.priority
	const for_user_uid = uiLstSprints.filter.for_user_uid
	const user_uid = uiLstSprints.filter.user_uid
	const orderBy = uiLstSprints.filter.orderBy
	const orderDir = uiLstSprints.filter.orderDir

	useEffect(() => {
		// TODO: remove this line when the api is ready for this module
		uiLstSprints.rpg = 25

		actLoadSpecificMonthSprints(
			currentDateObject.unix(),
			calendarPanel
		).catch(console.error)

		// Function to cancel interval
		const cancelCheckAndLoad = () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current)
			}
		}

		// Function to check if any item in list is modified and reload items it if necessary
		const checkAndLoadItems = () => {
			if (withIntervalLoad) {
				if (errorKey !== 'not_found') {
					actLoadSpecificMonthSprints(true)
						.then(
							(res) =>
								res &&
								actLoadSpecificMonthSprints().catch(
									console.error
								)
						)
						.catch(console.error)
				} else if (errorKey === 'not_found') {
					cancelCheckAndLoad()
				}
			} else {
				cancelCheckAndLoad()
			}
		}

		// Polling interval to check for modifications every 5 seconds
		if (withIntervalLoad) {
			intervalIdRef.current = setInterval(checkAndLoadItems, 5000)
		} else {
			cancelCheckAndLoad()
		}

		// Clean up interval on component unmount
		return () => cancelCheckAndLoad()
	}, [
		currentDateObject,
		calendarPanel,
		withIntervalLoad,
		bySearch,
		byDel,
		byCategory,
		byProgress,
		byPriority,
		user_uid,
		for_user_uid,
		orderBy,
		orderDir,
		appType.appIs
	])

	return (
		<GeneralContentWrapper>
			<Space direction="vertical" style={{ width: '100%' }}>
				<Button
					type="link"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate(-1)}
				>
					{t('single.back-to-list-view')}
				</Button>
				<GeneralTitle text={t('__planning%_sprint')} />
			</Space>

			<Filter
				uiStore={uiLstSprints}
				addNew={() => {
					const newId = uuidv4()
					newSprint(newId).then(() => {
						navigate(`/sprints/${newId}/edit`)
					})
				}}
				progressOptions={sprintsLstProgressOptions}
			/>

			<CustomCalendar
				store={uiLstSprints}
				onChange={(date) => setCurrentDateObject(date)}
				onPanelChange={(date, mode) => {
					setCalendarPanel(mode)
					setCurrentDateObject(date)
				}}
			/>
		</GeneralContentWrapper>
	)
})
export default SprintsCalendar
